import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

const SellFlatIndirapuramPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "How quickly can I sell my flat in Indirapuram?",
      answer: "With 99prop.com's direct buying model, you can complete the sale within 48 hours once all documentation is verified. Unlike traditional brokers or listing portals, we buy directly, eliminating lengthy waiting periods."
    },
    {
      question: "Do I need to pay any brokerage to sell my flat in Indirapuram?",
      answer: "No, 99prop.com charges zero brokerage fees. We buy your property directly, which means you save the typical 1-2% broker commission that can amount to lakhs of rupees."
    },
    {
      question: "What documents are needed to sell flat in Indirapuram?",
      answer: "You'll need sale deed, encumbrance certificate, property tax receipts, NOC from society, approved building plans, latest electricity and water bills, and identity proofs. Our team guides you through the entire documentation process."
    },
    {
      question: "Which societies in Indirapuram have good resale value?",
      answer: "Properties in ATS societies, Gaur City, Aditya Urban Homes, Shipra Sun City, and Express Garden Arcade typically command premium prices due to excellent maintenance, location, and amenities."
    },
    {
      question: "Will I get a fair price if I sell directly to 99prop.com?",
      answer: "Yes, our AI-powered valuation system analyzes current Indirapuram market rates, recent transactions, property condition, and location to offer competitive market prices. You can get a free valuation instantly on our website."
    },
    {
      question: "Is selling to 99prop.com better than listing on property portals?",
      answer: "Absolutely. Listing portals only advertise your property while you wait for buyers, pay brokerage, and handle negotiations. We offer direct purchase with zero brokerage, faster closing, and complete transparency."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": "99Prop",
    "description": "Direct property buying platform with zero brokerage and 48-hour closing in NCR region",
    "url": "https://99prop.com",
    "telephone": "+91-9891979097",
    "email": "info@99prop.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Indirapuram",
      "addressRegion": "Ghaziabad",
      "addressCountry": "IN"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Ghaziabad"
      },
      {
        "@type": "City",
        "name": "Indirapuram"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "520"
    }
  };

  return (
    <>
      <Helmet>
        <title>Sell Flat in Indirapuram | Zero Brokerage | 48-Hour Sale</title>
        <meta name="description" content="Sell flat in Indirapuram directly to 99prop.com. Zero brokerage, 48-hour closing, instant valuation. ATS, Gaur City, Shipra properties welcomed." />
        <meta name="keywords" content="sell flat in Indirapuram, sell property Indirapuram, zero brokerage Indirapuram, property valuation Indirapuram, ATS societies, Gaur City, Shipra Sun City" />
        <link rel="canonical" href="https://99prop.com/sell-flat-in-indirapuram" />
        
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content="Sell Flat in Indirapuram | Zero Brokerage | 48-Hour Sale" />
        <meta property="og:description" content="Sell flat in Indirapuram directly to 99prop.com. Zero brokerage, 48-hour closing, instant valuation. ATS, Gaur City, Shipra properties welcomed." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://99prop.com/sell-flat-in-indirapuram" />
        <meta property="og:site_name" content="99prop.com" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sell Flat in Indirapuram | Zero Brokerage | 48-Hour Sale" />
        <meta name="twitter:description" content="Sell flat in Indirapuram directly to 99prop.com. Zero brokerage, 48-hour closing, instant valuation." />
      </Helmet>
      
      {/* JSON-LD Schema for FAQs */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-brand-500 to-brand-700 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Sell Flat in Indirapuram
              </h1>
              <p className="text-xl sm:text-2xl text-brand-100 mb-8 max-w-3xl mx-auto">
                Zero Brokerage | 48-Hour Closing | Direct Purchase | Fair Market Price
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/?start=true')}
                  className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl"
                >
                  Check Your Property Value
                </button>
                <a
                  href="#contact"
                  className="bg-brand-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-900 transition shadow-xl"
                >
                  Get Instant Quote
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <article className="bg-white rounded-2xl shadow-lg p-8 sm:p-12 space-y-8">
            
            {/* Introduction */}
            <section>
              <p className="text-lg text-gray-700 leading-relaxed">
                Looking to <strong>sell flat in Indirapuram</strong> without the hassle of brokers, endless property visits, and months of waiting? 99prop.com, operated by 99Prop Capital, offers a revolutionary direct buying model that eliminates intermediaries and closes deals in just 48 hours. Whether you own a property in ATS societies, Gaur City, Shipra Sun City, Aditya Urban Homes, or Express Garden, we provide fair market valuations and zero brokerage transactions.
              </p>
            </section>

            {/* Market Trends */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Current Property Prices in Indirapuram (2025)</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Indirapuram continues to be one of Ghaziabad's most sought-after residential destinations. The real estate market here has shown consistent growth, making it an excellent time to <strong>sell flat in Indirapuram</strong>.
              </p>
              <div className="bg-brand-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Average Price Per Sq.Ft by Location:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex justify-between"><span>• Aditya Urban Homes</span><span className="font-semibold">₹5,800 - ₹6,200/sq.ft</span></li>
                  <li className="flex justify-between"><span>• ATS Societies</span><span className="font-semibold">₹6,000 - ₹6,500/sq.ft</span></li>
                  <li className="flex justify-between"><span>• Gaur City</span><span className="font-semibold">₹5,500 - ₹6,000/sq.ft</span></li>
                  <li className="flex justify-between"><span>• Shipra Sun City</span><span className="font-semibold">₹5,700 - ₹6,100/sq.ft</span></li>
                  <li className="flex justify-between"><span>• Express Garden Arcade</span><span className="font-semibold">₹5,400 - ₹5,900/sq.ft</span></li>
                </ul>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4">
                These rates are current as of early 2025 and can vary based on floor level, property condition, amenities, and exact location within the society. Use our free AI-powered valuation tool to get an accurate estimate for your specific property.
              </p>
            </section>

            {/* 3-Step Selling Process */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Sell Your Flat in Just 3 Simple Steps</h2>
              <p className="text-gray-700 text-center mb-8 max-w-3xl mx-auto">
                No broker harassment. No endless negotiations. No months of waiting. Get cash in 48 hours with our streamlined process.
              </p>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-gradient-to-br from-brand-50 to-blue-50 p-8 rounded-xl text-center relative">
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-2xl shadow-lg">
                    1
                  </div>
                  <div className="mt-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Instant Valuation</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Fill our 2-minute AI-powered valuation form. Get your property's market value instantly, no site visit needed yet.
                    </p>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-brand-600 font-bold text-lg">⏱️ Time: 2 Minutes</div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl text-center relative">
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-2xl shadow-lg">
                    2
                  </div>
                  <div className="mt-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Document Verification</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Our legal team verifies title, NOC, property tax receipts. Physical inspection scheduled. All issues flagged upfront.
                    </p>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-green-600 font-bold text-lg">⏱️ Time: 24 Hours</div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-8 rounded-xl text-center relative">
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-2xl shadow-lg">
                    3
                  </div>
                  <div className="mt-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Receive Payment</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Agreement signed. Token advance paid same day. Full payment via bank transfer/cheque within 24 hours of registry.
                    </p>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-orange-600 font-bold text-lg">⏱️ Time: 24 Hours</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-gradient-to-r from-brand-500 to-brand-600 p-8 rounded-xl text-center">
                <h3 className="text-3xl font-bold text-white mb-3">Total Time: 48 Hours to Cash ✅</h3>
                <p className="text-brand-100 text-lg mb-6">
                  From valuation to money in your bank account - the fastest way to sell property in Indirapuram
                </p>
                <button
                  onClick={() => navigate('/?start=true')}
                  className="bg-white text-brand-600 px-10 py-5 rounded-lg font-bold text-xl hover:bg-gray-100 transition shadow-xl"
                >
                  Start Selling Now
                </button>
              </div>
            </section>

            {/* WhatsApp CTA */}
            <section className="bg-gradient-to-r from-green-500 to-green-600 p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-white mb-3">Urgent Sale? Message Us on WhatsApp</h3>
              <p className="text-green-100 mb-6">Get instant quote in under 5 minutes. Our team responds 24/7</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://wa.me/919891979097?text=Hi, I want to sell my flat in Indirapuram. Please provide a quote."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-green-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl inline-flex items-center justify-center gap-2"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp Now
                </a>
                <a
                  href="tel:+919891979097"
                  className="bg-green-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-800 transition shadow-xl"
                >
                  Or Call: +91 9891 97 90 97
                </a>
              </div>
              <p className="text-green-100 text-sm mt-4">⚡ Need money urgently? We can advance 30% within 6 hours of documentation.</p>
            </section>

            {/* Trust Signals */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Trusted by 520+ Property Sellers in NCR</h2>
              
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-brand-50 to-blue-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-brand-600 mb-2">520+</div>
                  <div className="text-gray-700 font-semibold">Properties Bought</div>
                  <div className="text-sm text-gray-600 mt-1">In Ghaziabad NCR</div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2">₹280Cr+</div>
                  <div className="text-gray-700 font-semibold">Transaction Value</div>
                  <div className="text-sm text-gray-600 mt-1">Paid to sellers in 2024-25</div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-orange-600 mb-2">48hrs</div>
                  <div className="text-gray-700 font-semibold">Average Closure</div>
                  <div className="text-sm text-gray-600 mt-1">From quote to payment</div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl text-center">
                  <div className="text-4xl font-bold text-purple-600 mb-2">4.8/5</div>
                  <div className="text-gray-700 font-semibold">Seller Rating</div>
                  <div className="text-sm text-gray-600 mt-1">Based on 520 reviews</div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-brand-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">✅ Legally Verified & Transparent Operations</h3>
                <p className="text-gray-700 text-center leading-relaxed">
                  All our purchases are backed by legal verification, and we provide complete documentation including sale deed drafting, NOC coordination, and registry assistance. Your transaction is 100% safe and transparent.
                </p>
              </div>
            </section>

            {/* Objection Handling */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Common Seller Concerns Addressed</h2>
              
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-brand-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "Will I get market rate or will 99prop buy at a discount?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>You'll get 95-98% of market rate.</strong> Our AI valuation considers 5,000+ recent transactions, floor, facing, society reputation, and current market conditions. We offer 95-98% of this valuation because we buy directly (no broker margins), and we handle legal verification, documentation, and resale risks. Traditional brokers charge 1-2% anyway, so you're effectively getting market rate minus our service fee. Unlike distress buyers who offer 70-80%, we're transparent and fair.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-green-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "Why does 99prop want to buy my property? Is this a scam?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>We're a professional property investment company.</strong> Our business model: Buy properties at fair rates, hold for 6-12 months, and resell to end users with zero brokerage. We make money through volume (40-50 transactions/month) and appreciation, not by lowballing sellers. Read 520+ verified reviews, or speak to our past sellers for references. We're backed by ₹25 crore capital and operate with full transparency.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-blue-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "What if I find a better offer after agreeing with 99prop?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>You can walk away before token advance.</strong> Until we pay the token advance (typically 10% of property value), you're free to explore other options. Once token is paid, we expect commitment as per the agreement. We're confident our offer will be competitive—most sellers who get outside valuations confirm our AI pricing is accurate. If you do get a significantly better offer, we'll match it if the buyer is verified and serious.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-purple-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "Can I negotiate the price further?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Limited negotiation scope (1-2%).</strong> Our AI valuation is already market-competitive. However, if your property has exceptional factors not captured by our system (recent premium renovation, attached terrace, corner unit with 3-side open), share photos and details—we may revise upward by 1-2%. Conversely, if there are legal issues (pending NOC, property tax dues, structural damage), the price may be adjusted downward. Transparency works both ways.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-orange-500">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">❓ "What documents do I need to sell my flat?"</h3>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>We'll guide you through everything.</strong> Basic documents: Original sale deed, property tax receipts (last 3 years), society NOC, encumbrance certificate (we can arrange), latest electricity/water bills, Aadhaar/PAN of all owners. If any document is missing or outdated, our team will assist in obtaining it. We've handled 520+ transactions—document procurement is routine for us.
                  </p>
                </div>
              </div>

              <div className="mt-8 bg-gradient-to-r from-brand-500 to-brand-600 p-8 rounded-xl text-center">
                <h3 className="text-2xl font-bold text-white mb-3">Still Unsure? Talk to Our Seller Success Manager</h3>
                <p className="text-brand-100 mb-6">Get all your questions answered before making a decision</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="tel:+919891979097"
                    className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl"
                  >
                    Call Now: +91 9891 97 90 97
                  </a>
                  <button
                    onClick={() => navigate('/?start=true')}
                    className="bg-brand-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-800 transition shadow-xl"
                  >
                    Get Free Valuation
                  </button>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gradient-to-r from-brand-500 to-brand-600 p-8 rounded-xl text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Get Your Free Property Valuation</h3>
              <p className="text-brand-100 mb-6">Know your property's exact market value in 60 seconds</p>
              <button
                onClick={() => navigate('/?start=true')}
                className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl"
              >
                Check Property Value Now
              </button>
            </section>

            {/* Why Sell Directly */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Sell Directly to 99prop.com?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you decide to <strong>sell flat in Indirapuram</strong>, choosing the right selling method can save you lakhs of rupees and months of uncertainty. Here's why direct selling to 99prop.com is superior to traditional methods:
              </p>
              
              <div className="space-y-6">
                <div className="border-l-4 border-brand-500 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Zero Brokerage - Save 1-2%</h3>
                  <p className="text-gray-700">
                    Traditional brokers charge 1-2% commission on the sale price. For a ₹60 lakh flat, that's ₹60,000 to ₹1,20,000 gone in brokerage alone. With 99prop.com, you pay zero brokerage because we buy directly from you. Every rupee of the agreed price comes to you.
                  </p>
                </div>

                <div className="border-l-4 border-brand-500 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">48-Hour Closing Process</h3>
                  <p className="text-gray-700">
                    Forget waiting for months for the right buyer. Once your documentation is verified and our team conducts a physical inspection, we can close the deal within 48 hours. This is perfect for urgent financial needs, job relocations, or avoiding prolonged uncertainty.
                  </p>
                </div>

                <div className="border-l-4 border-brand-500 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Public Listings or Endless Visits</h3>
                  <p className="text-gray-700">
                    Your privacy matters. We don't list your property publicly or subject you to dozens of unqualified visitors. The transaction is discrete, professional, and involves minimal disruption to your daily life.
                  </p>
                </div>

                <div className="border-l-4 border-brand-500 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Fair Market Valuation</h3>
                  <p className="text-gray-700">
                    Our AI-powered valuation system analyzes recent Indirapuram transactions, property condition, location premiums, and current demand to offer you a competitive market price. You're not underselling your asset.
                  </p>
                </div>
              </div>
            </section>

            {/* How It Works */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Sell Flat in Indirapuram in 48 Hours</h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Get Instant Valuation</h3>
                    <p className="text-gray-700">Fill our 2-minute form with property details. Get AI-powered valuation instantly.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Documentation Review</h3>
                    <p className="text-gray-700">Our team reviews your property documents for legal clearance and title verification.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Physical Inspection</h3>
                    <p className="text-gray-700">A quick property inspection to assess actual condition and confirm details.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    4
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Receive Payment</h3>
                    <p className="text-gray-700">Sign the agreement and receive your payment. Deal closed in 48 hours.*</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4 italic">
                *Timeline subject to complete documentation and legal clearances.
              </p>
            </section>

            {/* Documents Checklist */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Required Documents to Sell Flat in Indirapuram</h2>
              <p className="text-gray-700 mb-4">
                Having these documents ready accelerates the selling process. Our team assists you in obtaining any missing paperwork.
              </p>
              <div className="bg-gray-50 p-6 rounded-xl">
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="text-brand-500 font-bold">✓</span>
                    <span className="text-gray-700"><strong>Original Sale Deed</strong> - Your proof of ownership</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brand-500 font-bold">✓</span>
                    <span className="text-gray-700"><strong>Encumbrance Certificate</strong> - Shows property is debt-free</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brand-500 font-bold">✓</span>
                    <span className="text-gray-700"><strong>Society NOC</strong> - No Objection Certificate from your society</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brand-500 font-bold">✓</span>
                    <span className="text-gray-700"><strong>Property Tax Receipts</strong> - Last 3 years cleared</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brand-500 font-bold">✓</span>
                    <span className="text-gray-700"><strong>Approved Building Plans</strong> - From Ghaziabad Development Authority</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brand-500 font-bold">✓</span>
                    <span className="text-gray-700"><strong>Utility Bills</strong> - Latest electricity and water bills</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brand-500 font-bold">✓</span>
                    <span className="text-gray-700"><strong>Identity & Address Proof</strong> - Aadhaar, PAN, passport</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-brand-500 font-bold">✓</span>
                    <span className="text-gray-700"><strong>Possession Letter</strong> - If applicable</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Why Indirapuram */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Indirapuram Properties Are in High Demand</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Indirapuram has evolved into a self-sufficient township with excellent connectivity to Delhi, Noida, and other NCR regions. When you <strong>sell flat in Indirapuram</strong>, you're offering buyers:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-brand-500 mt-1">•</span>
                  <span><strong>Proximity to Delhi:</strong> 20-25 minutes via NH-24 or Delhi-Meerut Expressway</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-500 mt-1">•</span>
                  <span><strong>Metro Connectivity:</strong> Upcoming metro stations improving access</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-500 mt-1">•</span>
                  <span><strong>Established Infrastructure:</strong> Schools, hospitals, malls, and entertainment hubs</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-500 mt-1">•</span>
                  <span><strong>Peaceful Living:</strong> Well-maintained societies with excellent amenities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-brand-500 mt-1">•</span>
                  <span><strong>Investment Potential:</strong> Consistent appreciation over the past decade</span>
                </li>
              </ul>
            </section>

            {/* FAQs */}
            <section>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-gray-200 pb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Final CTA */}
            <section className="bg-brand-500 p-8 rounded-xl text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Sell Your Indirapuram Flat?</h2>
              <p className="text-brand-100 text-lg mb-6">
                Get a free, no-obligation valuation in 60 seconds. Zero brokerage, 48-hour closing, complete transparency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/?start=true')}
                  className="bg-white text-brand-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-xl"
                >
                  Get Free Valuation
                </button>
                <a
                  href="tel:+919891979097"
                  className="bg-brand-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-brand-800 transition shadow-xl"
                >
                  Call: +91 9891 97 90 97
                </a>
              </div>
              <p className="text-brand-100 mt-6 text-sm">
                Operated by 99Prop Capital | Zero Brokerage | Direct Purchase
              </p>
            </section>

          </article>
        </div>
      </div>
    </>
  );
};

export default SellFlatIndirapuramPage;
